import { useCallback, useState, useReducer } from 'react';

import { SeoContentView, StorefrontCardView } from '@zola/svc-marketplace-ts-types';
import useEffectOnce from '@zola/zola-ui/src/hooks/useEffectOnce';

import { createAction, createReducer } from '@reduxjs/toolkit';

import { UserSettingsObjectType } from '~/actions/couples/types/userSettingsActionTypes';
import { fetchStorefrontSearchResults } from '~/libs/client/api/storefronts';
import { getSeoContentBlockHtml } from '~/libs/universal/seo';
import { fetchVendorSearchResultsPageData } from '~/libs/universal/vendorSearch';
import type { AccountVendorView } from '~/types/responseTypes';
import ApiService from '~/util/apiService';
import { getFacetsByVendorType } from '~/util/facetUtils';

import { buildBackendVendorSearchRequestFromSearchProperties } from '../../../util/buildSearchRequest';
import createCanonicalUrl, { createSeoCanonicalUrl } from '../../../util/canonicalUrl';
import {
  getSelectedFacetOptionsFromVendorSearch,
  ParseVendorSearchUrlResult,
} from '../../../util/parseVendorSearchUrl';
import { getSearchFilters } from '../components/SearchControls/searchFilters';
import type { VendorSearchRequest } from '../types/types';
import { getSelectedSrpFromSearch, trackVendorSearch } from '../utils/pageProviderHelpers';
import {
  DEFAULT_SEARCH_RESULTS_CONTEXT_VALUES,
  RecentlyViewedByTaxonomy,
  SearchResultsPageSearchDependentProps,
  SearchResultPageSearchResult,
  SearchResultsPageContext,
  SearchResultsPageContextValues,
  ShouldInsertMiniQuizByTaxonomy,
  SearchResultsPageUserAndSearchDependentProps,
  SearchResultsPageStaticProps,
  SearchResultsPageUserDependentProps,
} from './SearchResultsPageContext';

/**
 * Action when the taxonomy or location has changed
 */
const searchResultsPageTaxonomyOrLocationChange = createAction<
  Omit<ParseVendorSearchUrlResult, 'selectedFacetOptions'> &
    SearchResultsPageSearchDependentProps &
    // recentlyViewed is computed from recentlyViewedByTaxonomy and loading is set automatically when this action is dispatched
    Omit<SearchResultsPageUserAndSearchDependentProps, 'recentlyViewed' | 'loading'> & {
      // Note: Can update a single category
      recentlyViewedByTaxonomy: RecentlyViewedByTaxonomy;
    }
>('SEARCH_RESULTS_PAGE_CHANGE');

/**
 * Action for when a filter or sort has been applied, but the taxonomy and location are the same
 */
const searchResultsPageFilterChange = createAction<
  Omit<ParseVendorSearchUrlResult, 'selectedFacetOptions'> & {
    searchResults: SearchResultPageSearchResult;
    seoContent: SeoContentView | null;
  }
>('SEARCH_RESULTS_FILTER_CHANGE');

/**
 * Sets loading to true before executing searches
 */
const setLoading = createAction('SET_LOADING');

/**
 * The reducer is the source of truth for the page.  It is a combination of
 * initial values from the server, and updates from the user (dispatched through
 * various actions).
 */
const searchResultsPageState = createReducer<
  Omit<
    SearchResultsPageContextValues,
    'setVendorSearch' | 'updateWeddingPreferences' | 'updateVendorPreferences' | 'recentlyViewed'
  >
>({ ...DEFAULT_SEARCH_RESULTS_CONTEXT_VALUES }, (builder) => {
  builder
    .addCase(searchResultsPageTaxonomyOrLocationChange, (state, action) => {
      return {
        ...state,
        ...action.payload,
        selectedFacetOptions: getSelectedFacetOptionsFromVendorSearch(action.payload.vendorSearch),
        recentlyViewedByTaxonomy: {},
        loading: false,
      };
    })
    .addCase(searchResultsPageFilterChange, (state, action) => {
      return {
        ...state,
        ...action.payload,
        selectedFacetOptions: getSelectedFacetOptionsFromVendorSearch(action.payload.vendorSearch),
        loading: false,
      };
    })
    .addCase(setLoading, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
});

export type PreAuthSearchResultsPageProviderProps = SearchResultsPageStaticProps &
  SearchResultsPageSearchDependentProps &
  SearchResultsPageUserDependentProps & {
    storefrontToInquiryMap: Record<string, string>;

    /**
     * Does the user have a venue budget?  If not, we'll insert the budget quiz
     * on the venue SRP.
     */
    hasVenueBudget: boolean;

    /**
     * We hold out a certain percentage of users from seeing featured vendors
     */
    isInHoldoutGroupExperiment: boolean;

    /**
     * Vendors should never be held out from seeing featured vendors
     */
    identifiedAsVendor: boolean;

    isPreAuthSrp: boolean;

    /** Release ramp for pros and cons on the pre-auth vendor card */
    preAuthSrpProsAndCons: boolean;

    /**
     * Static (page load time) lookup of if the user should see the mini-quiz by category
     */
    shouldInsertMiniQuiz: ShouldInsertMiniQuizByTaxonomy;

    /**
     * The user's account vendor records, if any. Indicates which categories
     * are booked, which we need to determine mini-quiz insertion dynamically.
     */
    accountVendors: AccountVendorView[];

    /**
     * Lookup of recently viewed vendors by taxonomy, or for all taxonomies by the empty string
     *
     * This is populated on load for the current category (or all categories) and updated
     * as the search changes.
     */
    recentlyViewedByTaxonomy: RecentlyViewedByTaxonomy;

    /**
     * User settings loaded on the server to stick in redux on page load.
     */
    userSettings: UserSettingsObjectType;

    parsedUrl: ParseVendorSearchUrlResult;

    searchResults: SearchResultPageSearchResult;

    showTopPicks: boolean;
    topPicks: StorefrontCardView[];
  };

export const PreAuthSearchResultsPageProvider: React.FC<PreAuthSearchResultsPageProviderProps> = ({
  children,
  taxonomiesWithFacets,
  facetsByVendorType,
  identifiedAsVendor,
  isInHoldoutGroupExperiment,
  searchResults,
  parsedUrl,
  ...value
}) => {
  const pageSize = 48;

  const [state, dispatch] = useReducer(searchResultsPageState, {
    ...value,
    ...parsedUrl,
    searchResults,
    taxonomiesWithFacets,
    facetsByVendorType,
    loading: false,
  });

  /**
   * Trigger the initial product list viewed tracking
   */
  useEffectOnce(() => {
    const vendorSearchRequestForInitialTracking =
      buildBackendVendorSearchRequestFromSearchProperties({
        vendorSearch: parsedUrl.vendorSearch,
        pageSize,
        shouldInsertBudgetQuiz: false,
        shouldInsertMiniQuiz: false,
        identifiedAsVendor,
        isInHoldoutGroupExperiment,
      });

    trackVendorSearch(
      parsedUrl.vendorSearch,
      vendorSearchRequestForInitialTracking,
      searchResults.vendors
    );
  });

  // Keep track of the SRP, when the location or category changes, we'll need
  // to fetch some new content and update the data initially provided by the server
  const [previousSrp, setPreviousSrp] = useState<{
    taxonomyKey: string | undefined;
    locationSlug: string | undefined;
  }>(getSelectedSrpFromSearch(parsedUrl.vendorSearch));

  /**
   * This is the main callback in the provider that triggers updates to the page
   * when filters / category / location / page / changes.
   */
  const setVendorSearch = useCallback(
    (nextSearch: VendorSearchRequest) => {
      // First, set loading so we can provide feedback to the couple (blur effect)
      dispatch(setLoading());

      // Determine if we are changing category or taxonomy
      const newSrp = getSelectedSrpFromSearch(nextSearch);

      // Build the API request to execute the search
      const vendorSearchRequest = buildBackendVendorSearchRequestFromSearchProperties({
        vendorSearch: nextSearch,
        pageSize,
        shouldInsertBudgetQuiz: false,
        shouldInsertMiniQuiz: false,
        identifiedAsVendor,
        isInHoldoutGroupExperiment,
      });

      // Construct a promise that will run the search.  We are either changing
      // filters or we are changing category/location.  Both of those need to
      // execute the search, but changing category/location fetches a bunch more data
      const executeSearch: () => Promise<SearchResultPageSearchResult> = () => {
        return fetchStorefrontSearchResults(vendorSearchRequest)
          .then((searchResponse) => ({
            totalHits: searchResponse.totalHits,
            offset: searchResponse.offset,
            vendors: searchResponse.entities,
            shouldInsertMiniQuiz: false,
            shouldInsertBudgetQuiz: false,
          }))
          .then((result) => {
            // Trigger track product list viewed for the new results
            trackVendorSearch(nextSearch, vendorSearchRequest, result.vendors);
            return result;
          });
      };

      // Construct a promise to the search (and any other required fetches)
      let promise: Promise<unknown>;
      if (
        // Determine if category or location changed
        previousSrp?.taxonomyKey !== newSrp.taxonomyKey ||
        previousSrp?.locationSlug !== newSrp.locationSlug
      ) {
        // Category or location changed, we need to re-fetch quite a bit of data
        promise = Promise.all([
          // Fetch page and SEO meta-data (any data that only depends on location)
          fetchVendorSearchResultsPageData(nextSearch, ApiService),

          // Run the search
          executeSearch(),
        ]).then(
          ([
            [
              newTaxonomiesWithFacets,
              adjacentVendors,
              newRealWeddings,
              expertAdvice,
              searchLocationReviews,
              seoContent,
              seoTopLocations,
              seoNearbyCities,
              seoMetrics,
            ],
            newSearchResults,
          ]) => {
            const newFacetsByVendorType = getFacetsByVendorType(newTaxonomiesWithFacets);

            // Build the new search filters
            const searchFilters = getSearchFilters(nextSearch, newTaxonomiesWithFacets);

            // Compute new canonical URLS
            const canonicalUrl = createCanonicalUrl(nextSearch, newFacetsByVendorType);
            const seoCanonicalUrl = createSeoCanonicalUrl(nextSearch, newFacetsByVendorType);

            // Update the page with new data
            dispatch(
              searchResultsPageTaxonomyOrLocationChange({
                vendorSearch: nextSearch,
                searchFilters,
                canonicalUrl,
                seoCanonicalUrl,
                taxonomiesWithFacets: newTaxonomiesWithFacets,
                facetsByVendorType: newFacetsByVendorType,
                adjacentVendors,
                realWeddings: newRealWeddings,
                expertAdvice,
                seoContent,
                seoTopLocations,
                seoNearbyCities,
                seoMetrics,
                showTopPicks: false,
                topPicks: [],
                recentlyViewedByTaxonomy: {},
                searchResults: newSearchResults,
                searchLocationReviews,
              })
            );
          }
        );
      } else {
        // Much simpler when only filters change, just execute the search
        promise = Promise.all([
          executeSearch(),
          getSeoContentBlockHtml(nextSearch, ApiService),
        ]).then(([newSearchResults, seoContent]) => {
          // Compute new canonical URLS
          const canonicalUrl = createCanonicalUrl(nextSearch, facetsByVendorType);
          const seoCanonicalUrl = createSeoCanonicalUrl(nextSearch, facetsByVendorType);

          dispatch(
            searchResultsPageFilterChange({
              vendorSearch: nextSearch,
              canonicalUrl,
              seoCanonicalUrl,
              searchResults: newSearchResults,
              seoContent,
            })
          );
        });
      }

      // Run the search (and related fetches) then record what the last category/taxonomy we searched was
      promise.finally(() => {
        setPreviousSrp(newSrp);
      });
    },
    [
      facetsByVendorType,
      identifiedAsVendor,
      isInHoldoutGroupExperiment,
      pageSize,
      previousSrp?.taxonomyKey,
      previousSrp?.locationSlug,
      // userContext,
    ]
  );

  return (
    <SearchResultsPageContext.Provider
      value={{
        ...state,
        setVendorSearch,
        updateWeddingPreferences: () => null,
        updateVendorPreferences: () => null,
        recentlyViewed: [],
      }}
    >
      {children}
    </SearchResultsPageContext.Provider>
  );
};
